/**
 * Risk Analysis Data Models
 * Based on the Risk Analysis API documentation
 */

import { CompanyAddressDTO, RiskAnalysisDTO } from './companyModels';

export { CompanyAddressDTO };

// Risk Score DTO
export class RiskScoreDTO {
  constructor(data = {}) {
    this.riskName = data.riskName || data.risk_name || '';
    this.score = data.score || 0; // 0-100
    this.confidence = data.confidence || 0; // 0-1
    this.reasoning = data.reasoning || '';
    this.evidence = data.evidence || [];
    this.impactLevel = data.impactLevel || data.impact_level || 'Medium';
    this.category = data.category || '';
    this.mitigationSuggestions = data.mitigationSuggestions || data.mitigation_suggestions || [];
    this.contributingNewsIds = data.contributingNewsIds || data.contributing_news_ids || [];
  }

  toJSON() {
    return {
      riskName: this.riskName,
      score: this.score,
      confidence: this.confidence,
      reasoning: this.reasoning,
      evidence: this.evidence,
      impactLevel: this.impactLevel,
      category: this.category,
      mitigationSuggestions: this.mitigationSuggestions,
      contributingNewsIds: this.contributingNewsIds
    };
  }

  getScoreColor() {
    if (this.score >= 80) return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
    if (this.score >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' };
    if (this.score >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
  }

  getImpactLevelColor() {
    switch (this.impactLevel.toLowerCase()) {
      case 'high': return { text: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'medium': return { text: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' };
      case 'low': return { text: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' };
      default: return { text: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' };
    }
  }

  getConfidenceColor() {
    if (this.confidence >= 0.8) return 'text-green-600';
    if (this.confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  }
}

// Risk Analysis Request DTO
export class RiskAnalysisRequestDTO {
  constructor(data = {}) {
    this.ticker = data.ticker || '';
    this.companyName = data.companyName || data.company_name;
    this.daysBack = data.daysBack || data.days_back || 30;
    this.maxNewsArticles = data.maxNewsArticles || data.max_news_articles || 50;
    this.includeIndustryProfile = data.includeIndustryProfile !== undefined ? data.includeIndustryProfile : (data.include_industry_profile !== undefined ? data.include_industry_profile : true);
    this.enableAIAnalysis = data.enableAIAnalysis !== undefined ? data.enableAIAnalysis : (data.enable_ai_analysis !== undefined ? data.enable_ai_analysis : true);
    this.enableSentimentAnalysis = data.enableSentimentAnalysis !== undefined ? data.enableSentimentAnalysis : (data.enable_sentiment_analysis !== undefined ? data.enable_sentiment_analysis : true);
    this.saveToDatabase = data.saveToDatabase !== undefined ? data.saveToDatabase : (data.save_to_database !== undefined ? data.save_to_database : true);
    this.updateCompanyProfile = data.updateCompanyProfile !== undefined ? data.updateCompanyProfile : (data.update_company_profile !== undefined ? data.update_company_profile : true);
    this.confidenceThreshold = data.confidenceThreshold || data.confidence_threshold || 0.7;
    this.minRelevanceScore = data.minRelevanceScore || data.min_relevance_score || 0.1;
    this.focusRiskCategories = data.focusRiskCategories || data.focus_risk_categories || [];
    this.analysisContext = data.analysisContext || data.analysis_context;
    this.requestId = data.requestId || data.request_id;
    this.userId = data.userId || data.user_id;
    this.requestTimestamp = data.requestTimestamp || data.request_timestamp;
  }

  toJSON() {
    return {
      ticker: this.ticker,
      companyName: this.companyName,
      daysBack: this.daysBack,
      maxNewsArticles: this.maxNewsArticles,
      includeIndustryProfile: this.includeIndustryProfile,
      enableAIAnalysis: this.enableAIAnalysis,
      enableSentimentAnalysis: this.enableSentimentAnalysis,
      saveToDatabase: this.saveToDatabase,
      updateCompanyProfile: this.updateCompanyProfile,
      confidenceThreshold: this.confidenceThreshold,
      minRelevanceScore: this.minRelevanceScore,
      focusRiskCategories: this.focusRiskCategories,
      analysisContext: this.analysisContext,
      requestId: this.requestId,
      userId: this.userId,
      requestTimestamp: this.requestTimestamp
    };
  }

  validate() {
    const errors = [];
    
    if (!this.ticker) {
      errors.push('Ticker is required');
    }
    
    if (this.daysBack < 1 || this.daysBack > 365) {
      errors.push('Days back must be between 1 and 365');
    }
    
    if (this.maxNewsArticles < 1 || this.maxNewsArticles > 200) {
      errors.push('Max news articles must be between 1 and 200');
    }
    
    if (this.confidenceThreshold < 0 || this.confidenceThreshold > 1) {
      errors.push('Confidence threshold must be between 0 and 1');
    }
    
    if (this.minRelevanceScore < 0 || this.minRelevanceScore > 1) {
      errors.push('Min relevance score must be between 0 and 1');
    }
    
    return errors;
  }
}

// Risk Analysis Response DTO (alias for compatibility)
// This is just an alias - the actual RiskAnalysisDTO is in companyModels.js
export { RiskAnalysisDTO as RiskAnalysisResponseDTO } from './companyModels';

// Company Response with Risk Analysis DTO
export class CompanyWithRiskAnalysisDTO {
  constructor(data = {}) {
    this.success = data.success || false;
    this.message = data.message;
    this.data = data.data ? new CompanyRiskAnalysisDataDTO(data.data) : null;
    this.errorCode = data.errorCode || data.error_code;
    this.timestamp = data.timestamp || new Date().toISOString();
  }

  toJSON() {
    return {
      success: this.success,
      message: this.message,
      data: this.data?.toJSON(),
      errorCode: this.errorCode,
      timestamp: this.timestamp
    };
  }
}

// Company Risk Analysis Data DTO
export class CompanyRiskAnalysisDataDTO {
  constructor(data = {}) {
    this.ticker = data.ticker || '';
    this.name = data.name || '';
    this.description = data.description || '';
    this.industry = data.industry || '';
    this.keywords = data.keywords || [];
    this.sector = data.sector;
    this.country = data.country;
    this.website = data.website;
    this.supplyChainRisks = data.supplyChainRisks || data.supply_chain_risks || [];
    this.riskCategories = data.riskCategories || data.risk_categories || [];
    this.isUserMade = data.isUserMade || data.is_user_made;
    this.userId = data.userId || data.user_id;
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
    this.updatedAt = data.updatedAt || data.updated_at || new Date().toISOString();
    this.newsUpdateTimestamp = data.newsUpdateTimestamp || data.news_update_timestamp;
    this.riskAnalysisTimestamp = data.riskAnalysisTimestamp || data.risk_analysis_timestamp;
    this.latestRiskAnalysis = data.latestRiskAnalysis || data.latest_risk_analysis ? 
      new RiskAnalysisDTO(data.latestRiskAnalysis || data.latest_risk_analysis) : null;
    this.overallRiskScore = data.overallRiskScore || data.overall_risk_score;
    this.supplyChainRiskScore = data.supplyChainRiskScore || data.supply_chain_risk_score;
    this.industryRiskScore = data.industryRiskScore || data.industry_risk_score;
    this.newsSentimentScore = data.newsSentimentScore || data.news_sentiment_score;
    this.analyzedNewsIds = data.analyzedNewsIds || data.analyzed_news_ids || [];
    this.totalNewsArticles = data.totalNewsArticles || data.total_news_articles;
    this.positiveNewsCount = data.positiveNewsCount || data.positive_news_count;
    this.negativeNewsCount = data.negativeNewsCount || data.negative_news_count;
    this.neutralNewsCount = data.neutralNewsCount || data.neutral_news_count;
    this.marketCap = data.marketCap || data.market_cap;
    this.employeeCount = data.employeeCount || data.employee_count;
    this.userAddedAddresses = (data.userAddedAddresses || data.user_added_addresses || [])
      .map(addr => new CompanyAddressDTO(addr));
  }

  toJSON() {
    return {
      ticker: this.ticker,
      name: this.name,
      description: this.description,
      industry: this.industry,
      keywords: this.keywords,
      sector: this.sector,
      country: this.country,
      website: this.website,
      supplyChainRisks: this.supplyChainRisks,
      riskCategories: this.riskCategories,
      isUserMade: this.isUserMade,
      userId: this.userId,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      newsUpdateTimestamp: this.newsUpdateTimestamp,
      riskAnalysisTimestamp: this.riskAnalysisTimestamp,
      latestRiskAnalysis: this.latestRiskAnalysis?.toJSON(),
      overallRiskScore: this.overallRiskScore,
      supplyChainRiskScore: this.supplyChainRiskScore,
      industryRiskScore: this.industryRiskScore,
      newsSentimentScore: this.newsSentimentScore,
      analyzedNewsIds: this.analyzedNewsIds,
      totalNewsArticles: this.totalNewsArticles,
      positiveNewsCount: this.positiveNewsCount,
      negativeNewsCount: this.negativeNewsCount,
      neutralNewsCount: this.neutralNewsCount,
      marketCap: this.marketCap,
      employeeCount: this.employeeCount,
      userAddedAddresses: this.userAddedAddresses.map(addr => addr.toJSON())
    };
  }
}

// Risk Categories Enum
export const RISK_CATEGORIES = {
  SUPPLY_CHAIN: 'supply_chain',
  MANUFACTURING: 'manufacturing',
  REGULATORY: 'regulatory',
  MARKET_COMPETITION: 'market_competition',
  TECHNOLOGY_DISRUPTION: 'technology_disruption',
  CYBERSECURITY: 'cybersecurity',
  GEOPOLITICAL: 'geopolitical',
  FINANCIAL: 'financial',
  OPERATIONAL: 'operational',
  ENVIRONMENTAL: 'environmental',
  INTELLECTUAL_PROPERTY: 'intellectual_property'
};

// Impact Levels Enum
export const IMPACT_LEVELS = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
};

// Model Factory Functions
export const createRiskAnalysisRequest = (data) => new RiskAnalysisRequestDTO(data);
export const createRiskAnalysisResponse = (data) => new RiskAnalysisDTO(data);
export const createCompanyWithRiskAnalysis = (data) => new CompanyWithRiskAnalysisDTO(data);
export const createRiskScore = (data) => new RiskScoreDTO(data);

// Utility Functions
export const getRiskScoreColor = (score) => {
  if (score >= 80) return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
  if (score >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' };
  if (score >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
  return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
};

export const getConfidenceColor = (confidence) => {
  if (confidence >= 0.8) return 'text-green-600';
  if (confidence >= 0.6) return 'text-yellow-600';
  return 'text-red-600';
};

export const getImpactLevelColor = (impactLevel) => {
  switch (impactLevel?.toLowerCase()) {
    case 'high': return { text: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
    case 'medium': return { text: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' };
    case 'low': return { text: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' };
    default: return { text: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' };
  }
};

export const formatRiskScore = (score) => {
  return Math.round(score * 100) / 100;
};

export const formatConfidence = (confidence) => {
  return Math.round(confidence * 100);
};
